import { ChangeEvent, FC, useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import { Flex } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import {
  AdditionalFileModal,
  AuthorizedButton,
  Container,
  DataTable,
  ExpeditionCertificateModal,
  PageTitle,
  SExportButton,
} from '../../../components';
import { tableActions } from '../../../store/reducers/tableReducer';
import styles from './index.module.scss';
import { INITIAL_VALUES } from './const';
import { useAppDispatch, useAppSelector } from '../../../store';
import useHttpService from '../../../services/hook';
import { getColumns } from './columns';
import validationSchema from './validations';
import { IExpandedRow } from '../../../components/DataTable/type';
import { IEnum } from '../../../services/type';
import {
  IFile,
  IWithViewPerm,
  IExpeditionCertificateDataSource,
  IExpeditionCertificateInitialValues,
} from '../../../types';
import { modalTypes } from '../../../assets/enums';
import { downloadFile } from '../../../helpers/File';
import { cleanupAfterSubmit, prepareModalData } from '../../../utils/functions';
import useFetchParameters from '../../../hooks';
import ExpeditionCertificateService from '../../../api-clients/ExpeditionCertificateService';
import EnumService from '../../../api-clients/EnumService';
import AuthService from '../../../api-clients/AuthService';

const ExpeditionCertificate: FC = () => {
  const dispatch = useAppDispatch();
  const executeService = useHttpService();
  const organizations = useAppSelector(
    (state) => state.organizationReducer.organizations,
  );

  // Redux State
  const search = useAppSelector((state) => state.tableReducer.search);
  const sort = useAppSelector((state) => state.tableReducer.sort);
  const page = useAppSelector((state) => state.tableReducer.page);

  // State
  const [selectedItem, setSelectedItem] = useState<
    IExpeditionCertificateDataSource | undefined
  >(undefined);
  const [additionalFile, setAdditionalFile] = useState<IFile[] | undefined>(
    undefined,
  );
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [dataSource, setDataSource] = useState<
    IExpeditionCertificateDataSource[]
  >([]);
  const [expandedRows, setExpandedRows] = useState<IExpandedRow[]>([]);
  const [unitOfGoods, setUnitOfGoods] = useState<IEnum[]>([]);
  const [currencies, setCurrencies] = useState<IEnum[]>([]);
  const [vehicleTypes, setVehicleTypes] = useState<IEnum[]>([]);
  const [statusChoices, setStatusChoices] = useState<IEnum[]>([]);
  const [transportationTypes, setTransportationTypes] = useState<IEnum[]>([]);
  const [additionalFileID, setAdditionalFileID] = useState<number | undefined>(
    undefined,
  );
  const [tempAdditionalFiles, setTempAdditionalFiles] = useState<
    FileList | undefined
  >(undefined);
  const [withViewPerm, setWithViewPerm] = useState<IWithViewPerm[]>([]);

  const parameters = useFetchParameters({ search, sort, page });
  const expeditionCertificateService = useMemo(
    () => new ExpeditionCertificateService(executeService, parameters),
    [executeService, parameters],
  );
  const authService = useMemo(
    () => new AuthService(executeService),
    [executeService],
  );
  const enumService = useMemo(
    () => new EnumService(executeService),
    [executeService],
  );

  // Handlers
  const handleFetchWithViewPerm = async () => {
    const organizationId = formik.values.organization;
    const response = await authService.fetchWithViewPerm(
      'expeditioncertificate',
      organizationId,
    );
    if (response && 'results' in response) {
      setWithViewPerm(response.results);
    }
  };

  const handleFetchEnums = async () => {
    const [
      unitOfGoodsResponse,
      vehicleTypesResponse,
      currenciesResponse,
      statusChoicesResponse,
      transportationTypesResponse,
    ] = await Promise.all([
      enumService.fetchUnitOfGoods(),
      enumService.fetchVehicleTypes(),
      enumService.fetchCurrencies(),
      enumService.fetchPoliciesStatusChoices(),
      enumService.fetchExpeditionCertificatesTransportationTypes(),
    ]);

    if (unitOfGoodsResponse && Array.isArray(unitOfGoodsResponse)) {
      setUnitOfGoods(unitOfGoodsResponse);
    }
    if (vehicleTypesResponse && Array.isArray(vehicleTypesResponse)) {
      setVehicleTypes(vehicleTypesResponse);
    }
    if (currenciesResponse && Array.isArray(currenciesResponse)) {
      setCurrencies(currenciesResponse);
    }
    if (statusChoicesResponse && Array.isArray(statusChoicesResponse)) {
      setStatusChoices(statusChoicesResponse);
    }
    if (
      transportationTypesResponse &&
      Array.isArray(transportationTypesResponse)
    ) {
      setTransportationTypes(transportationTypesResponse);
    }
  };

  const handleFetchData = async () => {
    const response = await expeditionCertificateService.fetchData();
    if (response && 'results' in response) {
      dispatch(tableActions.changeTableTotal(response.count));
      const results = response.results.reduce<
        IExpeditionCertificateDataSource[]
      >(
        (
          prev: IExpeditionCertificateDataSource[],
          cur: IExpeditionCertificateDataSource,
        ) => {
          return [...prev, { ...cur, key: cur.id }];
        },
        [],
      );
      setDataSource(results);
      if (additionalFileID) {
        const item = results.find((item) => item.id === additionalFileID);
        if (item) {
          setAdditionalFileID(item.id);
          setAdditionalFile(item.additional_files);
        }
      }
    }
  };

  const handleClickEye = async (id: number) => {
    dispatch(tableActions.changeTableRow(id));
    const response = await expeditionCertificateService.fetchExpandedRow(id);
    if (response && Array.isArray(response)) {
      setExpandedRows(response);
    }
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    formik.setErrors({});
    formik.setTouched({});
  };

  const handleOpenEditModal = async (id: number) => {
    const item = await expeditionCertificateService.fetchItemById(id);
    if (item && 'id' in item) {
      setSelectedItem(item);
    }
  };

  const handleCloseEditModal = () => {
    setSelectedItem(undefined);
    formik.setErrors({});
    formik.setTouched({});
  };

  const handleUploadFile = (e: ChangeEvent<HTMLInputElement>) => {
    formik.setFieldValue('upload_files', e.target.files);
  };

  const handleClickCancel = async (id: number) => {
    try {
      await expeditionCertificateService.updateStatus(
        id,
        'cancel_expected',
        [],
      );
    } catch (e) {
      console.log(e);
    } finally {
      cleanupAfterSubmit<IExpeditionCertificateDataSource>(
        setIsModalOpen,
        setSelectedItem,
        formik.resetForm,
        handleFetchData,
      );
    }
  };

  const handleClickDelete = async (id: number) => {
    try {
      await expeditionCertificateService.deleteItemById(id);
    } catch (e) {
      console.log(e);
    } finally {
      cleanupAfterSubmit<IExpeditionCertificateDataSource>(
        setIsModalOpen,
        setSelectedItem,
        formik.resetForm,
        handleFetchData,
      );
    }
  };

  const handleClickExport = async () => {
    try {
      const response = await expeditionCertificateService.exportData();
      if (response && 'download_url' in response) {
        downloadFile(response.download_url);
      }
    } catch (e) {
      console.log(e);
    }
  };

  const handleSubmit = async (values: IExpeditionCertificateInitialValues) => {
    try {
      await expeditionCertificateService.submitForm(values, selectedItem);

      if (selectedItem) {
        await expeditionCertificateService.updateStatus(
          selectedItem.id || 0,
          values.recent_status,
          values.email_users || [],
        );
      }
    } catch (e) {
      console.log(e);
    } finally {
      cleanupAfterSubmit<IExpeditionCertificateDataSource>(
        setIsModalOpen,
        setSelectedItem,
        formik.resetForm,
        handleFetchData,
      );
      dispatch(tableActions.changeTablePage({ number: 0, size: 20 }));
    }
  };

  const handleSetAdditionalFile = (id: number, file: IFile[]) => {
    setAdditionalFileID(id);
    setAdditionalFile(file);
  };

  const handleUploadAdditionalFile = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setTempAdditionalFiles(e.target.files);
    }
  };

  const handleCloseAdditionalFile = () => {
    setAdditionalFileID(undefined);
    setAdditionalFile(undefined);
    setTempAdditionalFiles(undefined);
  };

  const handleDeleteFile = async (id: number, fileId: number) => {
    try {
      await expeditionCertificateService.deleteFile(id, fileId);
    } catch (e) {
      console.log(e);
    } finally {
      handleFetchData();
    }
  };

  const handleSubmitFile = async () => {
    if (additionalFileID && tempAdditionalFiles) {
      try {
        await expeditionCertificateService.uploadFiles(
          additionalFileID,
          tempAdditionalFiles,
        );
      } catch (e) {
        console.log(e);
      } finally {
        setTempAdditionalFiles(undefined);
        handleFetchData();
      }
    }
  };

  // Columns
  const columns = getColumns(
    handleClickEye,
    handleOpenEditModal,
    handleClickDelete,
    handleClickCancel,
    handleSetAdditionalFile,
    organizations,
    currencies,
    statusChoices,
    transportationTypes,
  );

  // Form Management
  const formik = useFormik<IExpeditionCertificateInitialValues>({
    initialValues: INITIAL_VALUES,
    validationSchema,
    onSubmit: handleSubmit,
  });

  // Effects
  useEffect(() => {
    handleFetchEnums();
  }, []);

  useEffect(() => {
    if (formik.values.organization) {
      handleFetchWithViewPerm();
    }
  }, [formik.values.organization]);

  useEffect(() => {
    if (selectedItem) {
      const result = prepareModalData<
        IExpeditionCertificateDataSource,
        IExpeditionCertificateInitialValues
      >(selectedItem);
      result && formik.setValues(result);
    } else {
      formik.setValues(INITIAL_VALUES);
    }
  }, [selectedItem]);

  useEffect(() => {
    handleFetchData();
  }, [search, sort.key, sort.order, page.number, page.size]);

  // JSX Return
  return (
    <Container>
      <PageTitle title="Abonman Sefer Sertifikaları" />
      <Flex vertical gap="middle">
        <Flex justify="flex-end" gap={5}>
          <SExportButton onClick={handleClickExport} />
          <AuthorizedButton
            model="expeditioncertificate"
            action="add"
            className={styles.button}
            onClick={handleOpenModal}
          >
            <PlusOutlined className={styles.buttonIcon} />
            <div className={styles.buttonText}>Yeni Sefer Bildirimi</div>
          </AuthorizedButton>
        </Flex>
        <DataTable<IExpeditionCertificateDataSource>
          scrollable
          columns={columns}
          dataSource={dataSource}
          expandedRows={expandedRows}
          statusChoices={statusChoices}
        />
      </Flex>
      <ExpeditionCertificateModal
        type={modalTypes.CREATE}
        open={isModalOpen}
        title="Sefer Bildirimi Oluşturma"
        subTitle="Bu pencereden sefer bildirimi oluşturabilirsiniz." //todo: subtitle daha iyi hale sokulabilir
        formik={formik}
        organizations={organizations}
        handleUploadFile={handleUploadFile}
        handleSubmit={formik.handleSubmit}
        handleCancel={handleCloseModal}
        unitOfGoods={unitOfGoods}
        currencies={currencies}
        statusChoices={statusChoices}
        vehicleTypes={vehicleTypes}
        transportationTypes={transportationTypes}
        withViewPerm={withViewPerm}
      />
      <ExpeditionCertificateModal
        type={modalTypes.EDIT}
        open={!!selectedItem}
        title="Sefer Bildirimi Güncelleme"
        subTitle="Bu pencereden sefer bildirimini güncelleyebilirsiniz."
        formik={formik}
        organizations={organizations}
        handleUploadFile={handleUploadFile}
        handleSubmit={formik.handleSubmit}
        handleCancel={handleCloseEditModal}
        unitOfGoods={unitOfGoods}
        currencies={currencies}
        statusChoices={statusChoices}
        vehicleTypes={vehicleTypes}
        transportationTypes={transportationTypes}
        withViewPerm={withViewPerm}
      />
      <AdditionalFileModal
        open={!!additionalFileID && Array.isArray(additionalFile)}
        model="expeditioncertificateadditionalfile"
        tempAdditionalFiles={tempAdditionalFiles}
        additionalFiles={additionalFile}
        handleUploadFile={handleUploadAdditionalFile}
        handleDeleteFile={handleDeleteFile}
        handleSubmit={handleSubmitFile}
        handleCancel={handleCloseAdditionalFile}
      />
    </Container>
  );
};

export default ExpeditionCertificate;
